import React, { useEffect, useState, useMemo } from 'react';
import ReactDOM from 'react-dom';

interface BottomSheetOption {
  value: string;
  label: string;
  sublabel?: string;
  group?: string;
  disabled?: boolean;
  badge?: string;
}

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  options: BottomSheetOption[];
  selectedValue?: string;
  onSelect: (value: string) => void;
  searchable?: boolean;
  // Multi-select
  multiSelect?: boolean;
  selectedValues?: string[];
}

const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  options,
  selectedValue,
  onSelect,
  searchable = false,
  multiSelect = false,
  selectedValues = [],
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Reset search when sheet opens/closes
  useEffect(() => {
    if (!isOpen) setSearchTerm('');
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchTerm.trim()) return options;
    const term = searchTerm.toLowerCase();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(term) ||
        (opt.sublabel && opt.sublabel.toLowerCase().includes(term))
    );
  }, [options, searchTerm, searchable]);

  // Prevent body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSelect = (value: string) => {
    onSelect(value);
    if (!multiSelect) {
      onClose();
    }
  };

  const isSelected = (value: string) =>
    multiSelect ? selectedValues.includes(value) : selectedValue === value;

  const selectedCount = multiSelect ? selectedValues.length : 0;

  // Portal ile body'ye render et — parent overflow/position kısıtlamalarından kurtul
  return ReactDOM.createPortal(
    <div className="bottom-sheet-overlay" onClick={onClose}>
      <div
        className="bottom-sheet"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bottom-sheet-handle" />
        <div className="bottom-sheet-header">
          <h3>{title}</h3>
          <button className="bottom-sheet-close" onClick={onClose}>
            <i className="ri-close-line"></i>
          </button>
        </div>
        {searchable && (
          <div className="bottom-sheet-search">
            <i className="ri-search-line"></i>
            <input
              type="text"
              placeholder="Ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm('')}>
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>
        )}
        <div className="bottom-sheet-content">
          {filteredOptions.length === 0 ? (
            <div className="bottom-sheet-empty">
              <i className="ri-inbox-line"></i>
              <p>Seçenek bulunamadı</p>
            </div>
          ) : (
            <div className="bottom-sheet-options">
              {filteredOptions.map((option, index) => {
                const showGroupHeader = option.group &&
                  (index === 0 || filteredOptions[index - 1].group !== option.group);
                const optSelected = isSelected(option.value);

                return (
                  <React.Fragment key={option.value}>
                    {showGroupHeader && (
                      <div className="bottom-sheet-group-header">{option.group}</div>
                    )}
                    <button
                      className={`bottom-sheet-option ${optSelected ? 'selected' : ''} ${option.disabled ? 'disabled' : ''}`}
                      onClick={() => !option.disabled && handleSelect(option.value)}
                      disabled={option.disabled}
                    >
                      {multiSelect && (
                        <div className="bottom-sheet-option-check-left">
                          {option.disabled ? (
                            <i className="ri-forbid-line"></i>
                          ) : optSelected ? (
                            <i className="ri-checkbox-circle-fill"></i>
                          ) : (
                            <i className="ri-checkbox-blank-circle-line"></i>
                          )}
                        </div>
                      )}
                      <div className="bottom-sheet-option-content">
                        <span className="bottom-sheet-option-label">{option.label}</span>
                        {option.sublabel && (
                          <span className="bottom-sheet-option-sublabel">{option.sublabel}</span>
                        )}
                      </div>
                      {option.badge && (
                        <span className="bottom-sheet-option-badge">{option.badge}</span>
                      )}
                      {!multiSelect && optSelected && (
                        <i className="ri-check-line bottom-sheet-option-check"></i>
                      )}
                    </button>
                  </React.Fragment>
                );
              })}
            </div>
          )}
        </div>
        {multiSelect && (
          <div className="bottom-sheet-footer">
            <span className="bottom-sheet-footer-count">
              {selectedCount > 0 ? `${selectedCount} seçildi` : 'Seçim yapın'}
            </span>
            <button className="bottom-sheet-footer-btn" onClick={onClose}>
              Tamam
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default BottomSheet;
